import {
    buildEpisodeDisciplineLink,
    buildEpisodeLink,
} from '@/pages/PatientDetailsPage/PatientSchedulingTab/PatientEpisodeView/routes';
import { OrderType, Ordering, SyncState, VisitRequestWorkflowStep } from '@/types/graphql';
import { Utils } from '@/types/utils';
import { VisitOutcome, VisitState } from 'gql-types';
import merge from 'lodash/merge';
import moment from 'moment';

import { GraphInputHelpers } from '../../../fetching/graphql/GraphInputHelpers';
import { DashboardContainer, DashboardContainerDefinition, RootEntity } from './types';

export const DEFAULT_CONTAINER_FILTER_WINDOW_DAYS = 31;

export enum ContainerDefinitionType {
    CLINICIAN_CALL_OUTS = 'clinicianCallOuts',
    CLINICIAN_PATIENT_PAYER_MISMATCH = 'clinicianPatientPayerMismatch',
    CLINICIAN_UTILIZATION = 'clinicianUtilization',
    DISCHARGE = 'discharge',
    EVALS = 'evals',
    FREQUENCY_ORDERS = 'frequencyOrders',
    MISSED = 'missed',
    OBSERVATIONS = 'observations',
    OVERDUE_EPISODE_VISIT = 'overdue',
    PATIENT_SYNC_ISSUES = 'patientSyncIssues',
    PENDING_ADMISSION_PATIENTS = 'pendingAdmissionPatients',
    PENDING_END_OF_CARE = 'pendingEndOfCare',
    REASSIGN = 'reassign',
    RESCHEDULE = 'reschedule',
    SHIFT_BLOCK_SYNC_ISSUES = 'shiftBlockSyncIssues',
    SOCS = 'socs',
    TRANSFERS = 'transfers',
    UNASSIGNED = 'unassigned',
    UNASSIGNED_EPISODE_VISIT = 'unassignedEpisodeVisit',
    UNSYNCED_PATIENTS = 'unsyncedPatients',
    VISIT_CLINICIAN_APPOINTMENT_SYNC_ISSUES = 'visitClinicianAppointmentSyncIssues',
    VISIT_SYNC_ISSUES = 'visitSyncIssues',
}

export type ContainerDefinitionKey = ContainerDefinitionType | string;

export const defaultCliniciansRowToLink = (row: { id: string }): string => `/clinicians/${row.id}`;
export const defaultEpisodeOfCareRowToLink = (row: {
    id: string;
    patient: { id: string };
}): string => buildEpisodeLink({ patientId: row.patient.id, episodeId: row.id });
export const defaultOrdersRowToLink = (row: { id: string }): string => `/orders/${row.id}`;
export const defaultPatientsRowToLink = (row: { id: string }): string => `/patients/${row.id}`;
export const defaultVisitsRowToLink = (row: { id: string }): string => `/visits/${row.id}`;
export const defaultVisitRequestsRowToLink = (row: {
    id: string;
    visit: { id: string } | null;
}): string => (row.visit ? defaultVisitsRowToLink(row.visit) : `/visit-requests/${row.id}`);
export const defaultEpisodeOrdersRowToLink = ({
    patient: { id: patientId },
    discipline,
    episode,
}: {
    patient: { id: string };
    discipline: { id: string } | null;
    episode: { id: string } | null;
}) => {
    return buildEpisodeDisciplineLink(
        {
            patientId,
            episodeId: episode?.id,
        },
        discipline?.id,
    );
};
export const defaultEpisodeVisitsRowToLink = (row: {
    patient: { id: string };
    episode: { id: string };
}): string => buildEpisodeLink({ patientId: row.patient.id, episodeId: row.episode.id });

export const episodeVisitHighlightRowToLink = (row: {
    id: string;
    patient: { id: string };
    episode: { id: string } | null;
}): string =>
    row.episode
        ? `${buildEpisodeLink({ patientId: row.patient.id, episodeId: row.episode.id })}#visit-${
              row.id
          }`
        : defaultVisitsRowToLink(row);

// Highlight variant for variations without episodes (for example COFK).
// Route to the patient's scheduling tab and anchor to the visit.
export const patientVisitHighlightRowToLink = (row: {
    id: string;
    patient: { id: string };
}): string => `/patients/${row.patient.id}/scheduling#visit-${row.id}`;

export const defaultEpisodeVisitRequestsRowToLink = (row: {
    patient: { id: string };
    episode: { id: string };
}): string => buildEpisodeLink({ patientId: row.patient.id, episodeId: row.episode.id });

// Base definitions supply everything except `key`.
// The key is provided per-call by makeContainerDefinition so each variation gets its own persistence-safe key.
const CONTAINER_DEFINITIONS: Record<
    ContainerDefinitionType,
    Omit<DashboardContainerDefinition<RootEntity>, 'key'>
> = {
    [ContainerDefinitionType.CLINICIAN_CALL_OUTS]: {
        label: 'Clinician Call-Outs',
        tableConfig: {
            entity: RootEntity.CLINICIAN,
            filters: {
                shiftRecurrences: { isCanceled: { exact: true } },
            },
            ordering: { firstName: Ordering.ASC },
            rowToLink: defaultCliniciansRowToLink,
        },
    },
    [ContainerDefinitionType.CLINICIAN_PATIENT_PAYER_MISMATCH]: {
        label: 'Payer Mismatch',
        tableConfig: {
            entity: RootEntity.VISIT,
            filters: {
                clinicianAppointments: {
                    clinician: { id: { isNull: false } },
                    arePatientClinicianPayersCompatible: { exact: false },
                },
                outcome: { exact: VisitOutcome.NONE },
            },
            ordering: { patient: { fullName: Ordering.ASC } },
            emptyTableText: 'No payer mismatches at this time.',
            rowToLink: defaultVisitsRowToLink,
        },
        hoverText: 'Visits with clinician and patient payer mismatches.',
        heavyQuery: true,
    },
    [ContainerDefinitionType.SOCS]: {
        label: 'SOCs',
        tableConfig: {
            entity: RootEntity.VISIT,
            filters: { services: { name: { iContains: 'start of care' } } },
            rowToLink: defaultVisitsRowToLink,
            ordering: { patient: { fullName: Ordering.ASC } },
        },
    },
    [ContainerDefinitionType.EVALS]: {
        label: 'Evals',
        tableConfig: {
            entity: RootEntity.VISIT,
            filters: { services: { name: { iContains: 'eval' } } },
            ordering: { patient: { fullName: Ordering.ASC } },
            rowToLink: defaultVisitsRowToLink,
        },
    },
    [ContainerDefinitionType.FREQUENCY_ORDERS]: {
        label: 'Frequency Orders',
        tableConfig: {
            entity: RootEntity.ORDER,
            filters: {
                orderType: { exact: OrderType.FREQUENCY },
                activeVisitRequests: {
                    workflowStep: {
                        inList: [
                            VisitRequestWorkflowStep.NEEDS_SERVICES,
                            VisitRequestWorkflowStep.NEEDS_CLINICIAN,
                        ],
                    },
                },
            },
            ordering: { patient: { fullName: Ordering.ASC } },
            rowToLink: defaultOrdersRowToLink,
        },
    },
    [ContainerDefinitionType.OVERDUE_EPISODE_VISIT]: {
        label: 'Overdue',
        tableConfig: {
            entity: RootEntity.VISIT,
            filters: {
                state: { inList: [VisitState.SCHEDULED, VisitState.CONFIRMED] },
                startDatetime: { lt: GraphInputHelpers.datetime(moment().subtract(15, 'minutes')) },
            },
            rowToLink: defaultVisitsRowToLink,
            ordering: {
                startDatetime: Ordering.ASC,
            },
            emptyTableText: 'No visits overdue by more than 15 mins.',
        },
        hoverText: 'Upcoming visits more than 15 mins. overdue.',
        extraProps: {
            showClinicianColumn: true,
        },
    },
    [ContainerDefinitionType.PENDING_ADMISSION_PATIENTS]: {
        label: 'Pending Admission Patients',
        tableConfig: {
            entity: RootEntity.PATIENT,
            filters: { episodes: { id: { isNull: true } } },
            ordering: { createdAt: Ordering.DESC },
            emptyTableText: 'No pending patients at this time.',
            rowToLink: defaultPatientsRowToLink,
        },
        hoverText: 'New referrals.',
    },
    [ContainerDefinitionType.PENDING_END_OF_CARE]: {
        label: 'Pending End of Care',
        tableConfig: {
            entity: RootEntity.PATIENT,
            filters: {
                episodes: {
                    endDate: {
                        range: {
                            start: GraphInputHelpers.date(new Date()),
                            end: GraphInputHelpers.date(
                                new Date(new Date().setDate(new Date().getDate() + 30)),
                            ),
                        },
                    },
                },
            },
            ordering: { fullName: Ordering.ASC },
            rowToLink: defaultPatientsRowToLink,
        },
    },
    [ContainerDefinitionType.TRANSFERS]: {
        label: 'Transfers',
        tableConfig: {
            entity: RootEntity.PATIENT,
            filters: { visits: { services: { name: { iContains: 'transfer' } } } },
            ordering: { fullName: Ordering.ASC },
            rowToLink: defaultPatientsRowToLink,
        },
    },
    [ContainerDefinitionType.OBSERVATIONS]: {
        label: 'Observations',
        tableConfig: {
            entity: RootEntity.PATIENT,
            filters: { notes: { iContains: 'obs' } },
            rowToLink: defaultPatientsRowToLink,
            ordering: { fullName: Ordering.ASC },
        },
    },
    [ContainerDefinitionType.RESCHEDULE]: {
        label: 'Reschedule',
        tableConfig: {
            entity: RootEntity.VISIT,
            filters: {
                state: { exact: VisitState.UNSCHEDULED },
                OR: { requiresReschedule: { exact: true } },
            },
            ordering: { patient: { fullName: Ordering.ASC } },
            rowToLink: defaultVisitsRowToLink,
        },
    },
    [ContainerDefinitionType.MISSED]: {
        label: 'Missed',
        tableConfig: {
            entity: RootEntity.VISIT,
            filters: { outcome: { exact: VisitOutcome.MISSED } },
            rowToLink: defaultVisitsRowToLink,
            ordering: { patient: { fullName: Ordering.ASC } },
        },
    },
    [ContainerDefinitionType.PATIENT_SYNC_ISSUES]: {
        label: 'Patient Sync Issues',
        tableConfig: {
            entity: RootEntity.PATIENT,
            filters: { syncStatus: { state: { exact: SyncState.FAILING } } },
            emptyTableText: 'No sync issues at this time',
            rowToLink: defaultPatientsRowToLink,
            ordering: { fullName: Ordering.ASC },
        },
        hoverText: 'Patients unable to be synced to EMR.',
        heavyQuery: true,
    },
    [ContainerDefinitionType.REASSIGN]: {
        label: 'Reassign',
        tableConfig: {
            entity: RootEntity.VISIT,
            filters: { needsClinicians: { exact: true } },
            rowToLink: defaultVisitsRowToLink,
            ordering: { patient: { fullName: Ordering.ASC } },
        },
    },
    [ContainerDefinitionType.UNASSIGNED]: {
        label: 'Unassigned',
        tableConfig: {
            entity: RootEntity.VISIT,
            filters: { needsClinicians: { exact: true }, outcome: { exact: VisitOutcome.NONE } },
            emptyTableText: 'No unassigned visits at this time',
            rowToLink: defaultVisitsRowToLink,
            ordering: {
                startDatetime: Ordering.ASC,
            },
        },
        hoverText: 'Visits in need of a clinician assignment.',
    },
    [ContainerDefinitionType.UNASSIGNED_EPISODE_VISIT]: {
        label: 'Unassigned',
        tableConfig: {
            entity: RootEntity.VISIT,
            filters: { needsClinicians: { exact: true }, outcome: { exact: VisitOutcome.NONE } },
            emptyTableText: 'No unassigned visits at this time',
            rowToLink: defaultEpisodeVisitsRowToLink,
            ordering: {
                startDatetime: Ordering.ASC,
            },
        },
        hoverText: 'Visits in need of a clinician assignment.',
    },
    [ContainerDefinitionType.UNSYNCED_PATIENTS]: {
        label: 'Patient Sync Issues',
        tableConfig: {
            entity: RootEntity.PATIENT,
            filters: { externalIds: { value: { isNull: true } } },
            emptyTableText: 'No sync issues at this time',
            rowToLink: defaultPatientsRowToLink,
            ordering: { fullName: Ordering.ASC },
        },
        hoverText: 'Patients unable to be synced to EMR.',
    },
    [ContainerDefinitionType.DISCHARGE]: {
        label: 'Discharge',
        tableConfig: {
            entity: RootEntity.VISIT,
            filters: { services: { name: { iContains: 'discharge' } } },
            rowToLink: defaultVisitsRowToLink,
            ordering: { patient: { fullName: Ordering.ASC } },
        },
    },
    [ContainerDefinitionType.CLINICIAN_UTILIZATION]: {
        label: 'Clinician Utilization',
        tableConfig: {
            entity: RootEntity.CLINICIAN,
            filters: {},
            rowToLink: defaultCliniciansRowToLink,
            ordering: { firstName: Ordering.ASC },
        },
    },
    [ContainerDefinitionType.VISIT_CLINICIAN_APPOINTMENT_SYNC_ISSUES]: {
        label: 'Appointment Sync Issues',
        tableConfig: {
            entity: RootEntity.VISIT,
            filters: {
                clinicianAppointments: { syncStatus: { state: { exact: SyncState.FAILING } } },
                outcome: { exact: VisitOutcome.NONE },
            },
            rowToLink: defaultVisitsRowToLink,
            emptyTableText: 'No sync issues at this time.',
            ordering: { patient: { fullName: Ordering.ASC } },
        },
        hoverText: 'Visits unable to be synced to EMR.',
        heavyQuery: true,
    },
    [ContainerDefinitionType.VISIT_SYNC_ISSUES]: {
        label: 'Visit Sync Issues',
        tableConfig: {
            entity: RootEntity.VISIT,
            filters: {
                syncStatus: { state: { exact: SyncState.FAILING } },
                outcome: { exact: VisitOutcome.NONE },
            },
            emptyTableText: 'No sync issues at this time.',
            rowToLink: defaultVisitsRowToLink,
            ordering: { patient: { fullName: Ordering.ASC } },
        },
        hoverText: 'Visits unable to be synced to EMR.',
        heavyQuery: true,
    },
    [ContainerDefinitionType.SHIFT_BLOCK_SYNC_ISSUES]: {
        label: 'Shift Block Sync Issues',
        tableConfig: {
            entity: RootEntity.SHIFT_BLOCK,
            filters: {
                syncStatus: { state: { exact: SyncState.FAILING } },
            },
            emptyTableText: 'No shift block sync issues at this time.',
            rowToLink: null,
            ordering: { startDatetime: Ordering.DESC },
        },
        hoverText: 'Shift blocks that failed to sync to the external calendar system.',
        heavyQuery: true,
    },
};

// Per-variation `key` is required. Using a shared enum-string key would collide
// across variations in the backend snooze/toggle-pref tables.
export function makeContainerDefinition({
    type,
    key,
    defaultEnabled,
    overrides = {},
}: {
    type: ContainerDefinitionType;
    key: string;
    defaultEnabled: boolean;
    overrides?: Utils.RecursivePartial<DashboardContainer<RootEntity>>;
}): DashboardContainer<RootEntity> {
    const baseDefinition = CONTAINER_DEFINITIONS[type];
    return merge({}, baseDefinition, overrides, { defaultEnabled, key });
}
